import { Button, Grid } from '@mui/material';
import { useState } from 'react';
import ComboBox, { ComboBoxOption } from '../../../../components/form/controls/ComboBox';
import Popup from '../../../../components/Popup';
import ClientForm from './ClientForm';
import ClientQueries from './ClientQueries';
import useClients from '../../../../hooks/useClients';
import ConfirmDialog, { IConfirmDialog } from '../../../../components/form/controls/ConfirmDialog';
import { ClientDto } from '../../../../api';
import useServiceLineStyles from '../../../../styles/useServiceLinesStyles';
import Loading from '../../../../components/Loading';
import { getComboBoxOption, getComboBoxOptions } from '../../../../utils/Utils';
import CreateClientPopup from '../../../../components/form/popups/CreateClientPopup';

interface IClientsProps {
  selectedClient: ClientDto | undefined;
  setSelectedClient: (values: ClientDto | undefined) => void;
}

function Clients(props: IClientsProps) {
  const classes = useServiceLineStyles();
  const { selectedClient, setSelectedClient } = props;
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isUpdateFormOpen, setIsUpdateFormOpen] = useState(false);
  const [selectionError, setSelectionError] = useState('');
  const { mutateUpdateClient, deleteClientMutation } = ClientQueries();
  const { data: clients, isLoading } = useClients();
  const [comboBoxValue, setComboBoxValue] = useState<ComboBoxOption | undefined>(undefined);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    title: '',
    content: '',
  } as IConfirmDialog);

  /**
   * If the clients data is still loading, show a loading spinner
   */
  if (isLoading) {
    return <Loading />;
  }

  /**
   * Handles the client combo box selection changes
   * @param option the selected client combo box option
   * @returns
   */
  function handleClientSelectionChange(option: ComboBoxOption | null): void {
    if (option === null) {
      setComboBoxValue(undefined);
      return setSelectedClient(undefined);
    }

    const client = clients?.find(x => x.id === option.value);
    setComboBoxValue(option);
    setSelectedClient({
      id: option.value,
      name: option.label,
      projects: client?.projects ?? [],
    });
  }

  /**
   * Handles client deletion
   */
  const handleDeleteClient = () => {
    if (validate()) {
      setDeleteDialog({
        isOpen: true,
        title: `Are you sure you want to delete ${selectedClient?.name}?`,
        content: "You can't undo this operation",
        onConfirm: async () => {
          if (selectedClient !== undefined) {
            deleteClientMutation.mutate(selectedClient.id, {
              onSuccess: async () => {
                clearComboBox();
                deleteClientMutation.reset();
                setSelectedClient(undefined);
                closeDeleteDialog();
              },
            });
          }
        },
      });
    }
  };

  /**
   * Clears the combo box
   */
  const clearComboBox = () => {
    return new Promise(resolve => {
      resolve(setComboBoxValue(undefined));
    });
  };

  /**
   * Closes the delete dialog
   * @returns a promise once the dialog is closed
   */
  const closeDeleteDialog = () => {
    return new Promise(resolve => {
      resolve(
        setDeleteDialog({
          ...deleteDialog,
          isOpen: false,
        })
      );
    });
  };

  /**
   * Checks whether a client has been selected
   * @returns true if a client has been selected, else return false.
   */
  const validate = () => {
    if (selectedClient !== undefined) {
      setSelectionError('');
      return true;
    }
    setSelectionError('Select a client');
    return false;
  };

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h3>Clients</h3>

      <CreateClientPopup
        isOpen={isCreateFormOpen}
        setIsOpen={setIsCreateFormOpen}
        onSuccess={newClient => {
          const option = getComboBoxOption(newClient);
          setComboBoxValue(option);
          handleClientSelectionChange(option);
        }}
      />

      <Popup openPopup={isUpdateFormOpen} setOpenPopup={setIsUpdateFormOpen} title="Update Client">
        <ClientForm
          onCancel={() => setIsUpdateFormOpen(false)}
          onSubmit={values => {
            mutateUpdateClient(values, {
              onSuccess: updatedClient => {
                const option = getComboBoxOption(updatedClient);
                setComboBoxValue(option);
                setSelectedClient(updatedClient);
                setIsUpdateFormOpen(false);
              },
            });
          }}
          client={selectedClient}
        />
      </Popup>

      <ConfirmDialog confirmDialog={deleteDialog} setConfirmDialog={setDeleteDialog} />

      <Button
        variant="outlined"
        size="large"
        onClick={() => setIsCreateFormOpen(true)}
        className={classes.createButton}
      >
        Create
      </Button>

      <Grid container>
        <Grid item xs={4}>
          <ComboBox
            label={'Client'}
            name={'Client'}
            onChange={handleClientSelectionChange}
            value={comboBoxValue}
            options={getComboBoxOptions(clients)}
            error={selectionError}
          />
        </Grid>
        <Button
          className={classes.updateButton}
          variant="contained"
          size="large"
          onClick={() => validate() && setIsUpdateFormOpen(true)}
        >
          Update
        </Button>

        {/* Comment out until we figure out how delete should work (cascade deletion or other) [Task 564] 

          <Button
            className={classes.deleteButton}
            size="large"
            variant="contained"
            color="secondary"
            onClick={handleDeleteClient}
          >
            Delete
          </Button>
        */}
      </Grid>
    </div>
  );
}

export default Clients;
